import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

import { EChartsOption } from 'echarts';

import sortBy from 'lodash/sortBy';
import { Subscription, tap } from 'rxjs';
import { ScreenService } from 'app/shared/services/screen.service';
import { ColorUtils } from 'app/shared/utils/color.utils';
import { SearchResultsService } from '../../search-results.service';

@Component({
  selector: 'app-results-chart-view',
  templateUrl: 'results-chart-view.component.html',
  styleUrls: ['results-chart-view.component.scss'],
})
export class ResultsChartViewComponent implements OnChanges, OnDestroy {
  resultList: __esri.Graphic[];
  fieldsObservable = this.searchResultsService.onEstatisticsFieldsLoaded;

  @Input()
  fieldSelected: __esri.Field;
  @Output()
  fieldSelectedChange = new EventEmitter<__esri.Field>();

  private _currentFieldSelected: __esri.Field;

  barChartOptions: EChartsOption;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private resultListSubscription: Subscription;

  constructor(
    private searchResultsService: SearchResultsService,
    private screenService: ScreenService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fieldSelected) {
      if (this.fieldSelected !== this._currentFieldSelected) {
        this._currentFieldSelected = this.fieldSelected;
        this.handleResultList();
      }
    }
  }

  ngOnDestroy(): void {
    this.resultListSubscription?.unsubscribe();
  }

  onFieldSelected(item: __esri.Field) {
    this.handleResultList();

    if (this._currentFieldSelected !== item) {
      this._currentFieldSelected = item;
      this.fieldSelectedChange.emit(item);
    }
  }

  private handleResultList() {
    this.resultListSubscription?.unsubscribe();

    this.resultListSubscription = this.searchResultsService
      .observeSearchFeaturesAttr(this.fieldSelected?.name)
      .pipe(
        tap((results) => {
          const list = sortBy(results ?? [], 'alias');

          const labels = list.map((x) => x.alias);
          const data = list.map((x) => x.count);

          const fontFamily =
            "'Roboto', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif";
          const fontSize = 8;

          this.barChartOptions = {
            series: [
              {
                type: 'bar',
                data: data.map((value, index) => {
                  const color = ColorUtils.random();

                  return {
                    value,
                    itemStyle: {
                      color,
                    },
                  };
                }),
              },
            ],
            xAxis: {
              type: 'category',
              boundaryGap: true,
              data: labels,
              splitArea: {
                show: false,
              },
              splitLine: {
                show: true,
              },
              axisLabel: {
                fontFamily,
                fontSize,
                align: 'right',
                rotate: 52,
                hideOverlap: true,
                showMinLabel: true,
                showMaxLabel: true,
                formatter: (value, index) => {
                  const newValue = value.substring(0, 19);

                  if (newValue.length < value.length) {
                    value = newValue + '...';
                  }

                  return value;
                },
              },
              axisPointer: {
                snap: true,
              },
              axisTick: {
                alignWithLabel: true,
              },
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                fontFamily,
                fontSize,
              },
            },
            grid: {
              top: 10,
              left: 0,
              right: 1,
              bottom: 0,
              containLabel: true,
            },
            tooltip: {
              trigger: 'axis',
              triggerOn: 'mousemove',
              textStyle: {
                fontFamily,
                fontSize: 12,
              },
            },
            textStyle: {
              fontFamily,
              fontSize,
            },
          };
        })
      )
      .subscribe();
  }
}
